import {
    CheckCircleIcon,
    DocumentIcon,
    ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { AnimatePresence, motion, MotionConfig } from "framer-motion";
import React, { useEffect } from "react";
import { SecondaryCompactButton } from "./secondary-button";
import { formatFileSize, UploadedFile } from "./uploaded-file";

export const UPLOAD_DISMISS_MS = 4000;

const rowArriving = { opacity: 0, y: -8 };
const rowSettled = { opacity: 1, y: 0 };
const rowDeparting = { opacity: 0, height: 0, marginTop: 0, overflow: "hidden" };
const rowTransition = { type: "spring", stiffness: 500, damping: 30 } as const;

const countdownDrain = { scaleX: 0 };
const countdownTransition = {
    duration: UPLOAD_DISMISS_MS / 1000,
    ease: "linear",
} as const;

interface UploadStatusListProps {
    files: UploadedFile[];
    onDismiss: (fileId: string) => void;
}

interface RowProps {
    file: UploadedFile;
    onDismiss: (fileId: string) => void;
}

const useDismissAfterCountdown = (
    file: UploadedFile,
    onDismiss: RowProps["onDismiss"],
) => {
    useEffect(() => {
        if (file.status !== "completed") {
            return undefined;
        }
        const handle = setTimeout(() => onDismiss(file.id), UPLOAD_DISMISS_MS);
        return () => clearTimeout(handle);
    }, [file.id, file.status, onDismiss]);
};

const StatusIcon: React.FC<{ status: UploadedFile["status"] }> = ({ status }) => {
    if (status === "completed") {
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    }
    if (status === "error") {
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
    }
    return <DocumentIcon className="h-5 w-5 text-blue-500" />;
};

const ProgressBar: React.FC<{ progress: number }> = ({ progress }) => (
    <div className="mt-1 flex items-center">
        <div className="h-2 flex-1 rounded-full bg-gray-200">
            <div
                className="h-2 rounded-full bg-blue-600 transition-all duration-300"
                style={{ width: `${progress}%` }}
            />
        </div>
        <span className="ml-2 text-xs text-gray-500">{progress}%</span>
    </div>
);

const Countdown: React.FC = () => (
    <motion.div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-0.5 origin-left rounded-b-lg bg-green-500"
        initial={false}
        animate={countdownDrain}
        transition={countdownTransition}
    />
);

const UploadRow: React.FC<RowProps> = ({ file, onDismiss }) => {
    useDismissAfterCountdown(file, onDismiss);
    return (
        <motion.li
            layout
            className="relative flex items-center overflow-hidden rounded-lg border border-gray-200 bg-white p-3"
            initial={rowArriving}
            animate={rowSettled}
            exit={rowDeparting}
            transition={rowTransition}>
            <div className="flex-shrink-0">
                <StatusIcon status={file.status} />
            </div>
            <div className="ml-3 min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900">{file.name}</p>
                <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                {file.status === "uploading" ? (
                    <ProgressBar progress={file.progress} />
                ) : null}
                {file.status === "error" && file.error ? (
                    <p className="mt-1 text-xs text-red-600">{file.error}</p>
                ) : null}
            </div>
            {file.status === "error" ? (
                <div className="ml-3 flex-shrink-0">
                    <SecondaryCompactButton
                        type="button"
                        onClick={() => onDismiss(file.id)}>
                        Dismiss
                    </SecondaryCompactButton>
                </div>
            ) : null}
            {file.status === "completed" ? <Countdown /> : null}
        </motion.li>
    );
};

const headingText = (files: UploadedFile[]): string => {
    const uploadingCount = files.filter(f => f.status === "uploading").length;
    if (uploadingCount > 0) {
        return `Uploading ${uploadingCount} of ${files.length}`;
    }
    return "Uploaded";
};

export const UploadStatusList: React.FC<UploadStatusListProps> = ({
    files,
    onDismiss,
}) => (
    <MotionConfig reducedMotion="user">
        <AnimatePresence initial={false}>
            {files.length > 0 ? (
                <motion.div
                    key="upload-status"
                    className="space-y-2"
                    initial={rowArriving}
                    animate={rowSettled}
                    exit={rowDeparting}
                    transition={rowTransition}>
                    <h4 className="text-sm font-medium text-gray-900">
                        {headingText(files)}
                    </h4>
                    <ul className="space-y-2">
                        <AnimatePresence initial={false}>
                            {files.map(file => (
                                <UploadRow
                                    key={file.id}
                                    file={file}
                                    onDismiss={onDismiss}
                                />
                            ))}
                        </AnimatePresence>
                    </ul>
                </motion.div>
            ) : null}
        </AnimatePresence>
    </MotionConfig>
);
